import { useState, useEffect } from 'react';
import { SettingsNativeBridgeFactory } from '../bridge/SettingsNativeBridgeFactory';
import { GetSettingsResponse } from '../Contracts/responses';
import { SettingsLogger, logConstants, AotCategory } from '../utils/SettingsLogger';
import {
	getFilteredSettingIds,
	expandGroupSettingIds,
	resolveVisibleParentIds,
} from '../config/DynamicSettingsFilter';
import { SettingsCategory } from '../config/SettingsCategory';
import {
	useFlyoverSafe,
	FlyoverType,
} from '../components/FlyoverNotification/FlyoverNotificationContext';
import { settingsCache, SettingEntry } from '../cache/SettingsCache';

interface SettingState {
	value: any;
	disabled: boolean;
	initialValue?: any;
	hidden: boolean;
}

interface UseSettingsPageOptions {
	category: SettingsCategory;
	defaultValue?: any;
	revertOnError?: boolean;
}

interface UseSettingsPageResult {
	settingsState: Record<string, SettingState>;
	availableSettingIds: string[];
	error: string | null;
	handleChangeBySettingId: (settingId: string, value: unknown) => Promise<void>;
	isLoading: boolean;
	restoreDefaults: () => Promise<void>;
}

/**
 * Shared hook for managing settings pages with dynamic loading, state management, and updates
 * Reduces code duplication across all settings pages
 * Combines functionality from both toggle-based and general settings management
 * Visibility of settings is controlled by the native layer via the hidden property in getSettings response
 */
export const useSettingsPage = ({
	category,
	defaultValue = true,
	revertOnError = false,
}: UseSettingsPageOptions): UseSettingsPageResult => {
	const [settingsState, setSettingsState] = useState<Record<string, SettingState>>({});
	const [availableSettingIds, setAvailableSettingIds] = useState<string[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const { showFlyover } = useFlyoverSafe();

	useEffect(() => {
		loadSettings();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [category]);

	useEffect(() => {
		const bridge = SettingsNativeBridgeFactory.getBridge();
		const cleanup = bridge.onRefreshSetting(async (settingKey: string) => {
			const platform = bridge.getPlatform();
			const entrypoint = bridge.getEntrypoint();
			const filteredIds = getFilteredSettingIds({ category, platform, entrypoint });
			const fetchIds = expandGroupSettingIds(filteredIds);

			if (fetchIds.includes(settingKey)) {
				// Setting belongs to the current (visible) category — update state and cache.
				try {
					const response = await bridge.getSetting({ settingKey });
					if (response.status === 0 && response.data) {
						const { settingValue, enabled, hidden } = response.data;
						let value: unknown = settingValue ?? defaultValue;
						if (value === 'true') value = true;
						if (value === 'false') value = false;

						setSettingsState((prev) => ({
							...prev,
							[settingKey]: {
								...prev[settingKey],
								value,
								disabled: enabled !== undefined ? !enabled : false,
								hidden: hidden === true,
							},
						}));

						// Update cache so re-visits also reflect the refreshed value
						const cached = settingsCache.get(category);
						if (cached) {
							const updatedSettings = cached.map((s) =>
								s.settingKey === settingKey
									? { ...s, settingValue: String(settingValue), enabled, hidden }
									: s
							);
							settingsCache.set(category, updatedSettings);
						}
					}
				} catch (err) {
					SettingsLogger.getInstance().write(
						logConstants.ERROR,
						`Failed to refresh setting ${settingKey}: ${err}`,
						AotCategory.Error,
						`Failed to refresh setting ${settingKey}: ${err}`
					);
				}
				return;
			}

			// Setting belongs to a different (inactive) category.
			// Find the owning category and update its cache so the next navigation there
			// serves the fresh value without a redundant bridge fetch.
			for (const otherCategory of Object.values(SettingsCategory)) {
				if (otherCategory === category) continue;

				const otherFilteredIds = getFilteredSettingIds({
					category: otherCategory,
					platform,
					entrypoint,
				});
				const otherFetchIds = expandGroupSettingIds(otherFilteredIds);

				if (!otherFetchIds.includes(settingKey)) continue;

				// Only update the cache if the category was previously populated — if it
				// was never visited the next visit will do a fresh fetch anyway.
				const cachedOther = settingsCache.get(otherCategory);
				if (!cachedOther) break;

				try {
					const response = await bridge.getSetting({ settingKey });
					if (response.status === 0 && response.data) {
						const { settingValue, enabled, hidden } = response.data;
						// Re-read after await in case a concurrent write invalidated it
						const freshCached = settingsCache.get(otherCategory);
						if (freshCached) {
							const updatedSettings = freshCached.map((s) =>
								s.settingKey === settingKey
									? { ...s, settingValue: String(settingValue), enabled, hidden }
									: s
							);
							settingsCache.set(otherCategory, updatedSettings);
							SettingsLogger.getInstance().write(
								logConstants.INFO,
								`Cache updated for inactive category ${otherCategory}, setting: ${settingKey}`,
								AotCategory.Info,
								`Cache updated for inactive category ${otherCategory}, setting: ${settingKey}`
							);
						}
					}
				} catch (err) {
					SettingsLogger.getInstance().write(
						logConstants.ERROR,
						`Failed to refresh setting ${settingKey} for inactive category ${otherCategory}: ${err}`,
						AotCategory.Error,
						`Failed to refresh setting ${settingKey} for inactive category ${otherCategory}: ${err}`
					);
				}
				break;
			}
		});
		return cleanup;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [category]);

	/**
	 * Converts a raw settings array (from cache or bridge) into component state.
	 * Extracted so it can be called from both the cache-hit and bridge-fetch paths.
	 */
	const buildStateFromSettings = (
		filteredIds: string[],
		fetchIds: string[],
		settingsArray: SettingEntry[]
	) => {
		const newState: Record<string, SettingState> = {};
		const visibleSubIds = new Set<string>();

		settingsArray.forEach((setting) => {
			let value: unknown = setting.settingValue ?? defaultValue;
			if (value === 'true') value = true;
			if (value === 'false') value = false;

			const isHidden = setting.hidden === true;
			newState[setting.settingKey] = {
				value,
				initialValue: value,
				disabled: setting.enabled !== undefined ? !setting.enabled : false,
				hidden: isHidden,
			};
			if (!isHidden) visibleSubIds.add(setting.settingKey);
		});

		fetchIds.forEach((id) => {
			if (!newState[id]) {
				newState[id] = { value: defaultValue, disabled: false, hidden: false };
				visibleSubIds.add(id);
			}
		});
		filteredIds.forEach((id) => {
			if (!newState[id]) {
				newState[id] = { value: defaultValue, disabled: false, hidden: false };
			}
		});

		setAvailableSettingIds(resolveVisibleParentIds(filteredIds, visibleSubIds));
		setSettingsState(newState);
	};

	const loadSettings = async () => {
		try {
			setIsLoading(true);
			const bridge = SettingsNativeBridgeFactory.getBridge();

			// Get platform and entrypoint from bridge
			const platform = bridge.getPlatform();
			const entrypoint = bridge.getEntrypoint();

			// Get filtered setting IDs for the category
			const filteredIds = getFilteredSettingIds({
				category,
				platform,
				entrypoint,
			});

			setAvailableSettingIds(filteredIds);

			if (filteredIds.length === 0) {
				SettingsLogger.getInstance().write(
					logConstants.INFO,
					`No ${category} settings available for platform: ${platform}, entrypoint: ${entrypoint}`,
					AotCategory.Info,
					`No ${category} settings available for platform: ${platform}, entrypoint: ${entrypoint}`
				);
				setIsLoading(false);
				return;
			}

			const fetchIds = expandGroupSettingIds(filteredIds);

			// ── Cache-first: serve from cache populated by useCategoryVisibility ──────
			const cachedSettings = settingsCache.get(category);
			if (cachedSettings) {
				SettingsLogger.getInstance().write(
					logConstants.INFO,
					`Cache hit for ${category} settings`,
					AotCategory.Info,
					`Cache hit for ${category} settings`
				);
				buildStateFromSettings(filteredIds, fetchIds, cachedSettings);
				setIsLoading(false);
				return;
			}

			// ── Cache miss: fetch from bridge and populate cache ───────────────────
			const settingsResponse = await bridge.getSettings({
				settings: fetchIds.map((settingKey) => ({ settingKey })),
			});

			SettingsLogger.getInstance().write(
				logConstants.INFO,
				`getSettings response for ${category} settings: ${JSON.stringify(
					settingsResponse
				)}`,
				AotCategory.Info,
				`getSettings response for ${category} settings: ${JSON.stringify(
					settingsResponse
				)}`
			);

			if (settingsResponse && settingsResponse.status === 0) {
				const getSettingsResp = settingsResponse as GetSettingsResponse;
				const settingsArray = getSettingsResp.data?.settings || [];

				// Populate cache for future visits to this category
				settingsCache.set(category, settingsArray);
				buildStateFromSettings(filteredIds, fetchIds, settingsArray);
			}
			setIsLoading(false);
		} catch (error) {
			const errorMessage = `Failed to load ${category} settings: ${error}`;
			SettingsLogger.getInstance().write(
				logConstants.ERROR,
				errorMessage,
				AotCategory.Error,
				errorMessage
			);
			setError(`Failed to load ${category} settings`);
			setIsLoading(false);
		}
	};

	const handleChangeBySettingId = async (settingId: string, value: unknown) => {
		try {
			// Update local state first for immediate UI feedback
			// For dropdown settings, preserve the full structure with availableOptions
			setSettingsState((prev) => {
				const prevValue = prev[settingId]?.value;
				let optimisticValue = value;

				// If updating with a simple string value and previous value has availableOptions, preserve them
				if (typeof value === 'string' && prevValue) {
					let prevData;
					try {
						prevData = typeof prevValue === 'string' ? JSON.parse(prevValue) : prevValue;
						if (prevData && prevData.availableOptions) {
							// Preserve the structure for dropdowns
							optimisticValue = JSON.stringify({
								selectedOption: value,
								availableOptions: prevData.availableOptions,
							});
						}
					} catch (e) {
						// If parsing fails, just use the new value as-is
					}
				}

				return {
					...prev,
					[settingId]: { ...prev[settingId], value: optimisticValue },
				};
			});
			setError(null);

			// Save the setting via the bridge
			const bridge = SettingsNativeBridgeFactory.getBridge();
			const response = await bridge.setSetting({
				settingKey: settingId,
				settingValue: value,
			});

			// Update state with the response value from native
			// This ensures we have the complete value structure (e.g., with availableOptions for dropdowns)
			if (response.status === 0 && response.data?.settingValue) {
				setSettingsState((prev) => ({
					...prev,
					[settingId]: {
						...prev[settingId],
						value: response.data!.settingValue,
					},
				}));
			}

			const successMessage = `Successfully saved setting ${settingId} with value: ${value}`;
			SettingsLogger.getInstance().write(
				logConstants.INFO,
				successMessage,
				AotCategory.Info,
				successMessage
			);
			// Invalidate cache so re-visiting this category fetches fresh data from native
			settingsCache.invalidate(category);
		} catch (error: unknown) {
			const errorMessage = `Error saving ${settingId}: ${error}`;
			SettingsLogger.getInstance().write(
				logConstants.ERROR,
				errorMessage,
				AotCategory.Error,
				errorMessage
			);
			showFlyover({
				type: FlyoverType.Error,
				message: 'Failed to update setting. Try again.',
			});
			setError(`Failed to save ${settingId} setting`);

			// Revert local state on error if requested
			if (revertOnError) {
				setSettingsState((prev) => ({
					...prev,
					[settingId]: {
						...prev[settingId],
						value: prev[settingId]?.initialValue ?? defaultValue,
					},
				}));
			}
		}
	};

	const restoreDefaults = async () => {
		try {
			setError(null);

			// Expand group parent IDs so sub-setting values are also sent to native.
			const idsToRestore = expandGroupSettingIds(availableSettingIds);

			// Prepare settings array with initial values to restore
			const settingsToRestore = idsToRestore
				.map((settingId) => {
					const initialValue = settingsState[settingId]?.initialValue ?? defaultValue;
					return {
						settingKey: settingId,
						settingValue: initialValue,
					};
				})
				.filter((setting) => setting.settingValue !== undefined);

			if (settingsToRestore.length === 0) {
				SettingsLogger.getInstance().write(
					logConstants.INFO,
					`No settings to restore for ${category}`,
					AotCategory.Info,
					`No settings to restore for ${category}`
				);
				return;
			}

			// Update local state first — expand to sub-setting IDs so sub-setting entries
			// are preserved (not wiped by a parent-only state replace).
			const restoredState: Record<string, SettingState> = {};
			expandGroupSettingIds(availableSettingIds).forEach((settingId) => {
				const initialValue = settingsState[settingId]?.initialValue ?? defaultValue;
				restoredState[settingId] = {
					value: initialValue,
					initialValue,
					disabled: settingsState[settingId]?.disabled ?? false,
					hidden: settingsState[settingId]?.hidden ?? false,
				};
			});
			setSettingsState((prev) => ({ ...prev, ...restoredState }));

			// Call setSettings API to restore all settings at once
			const bridge = SettingsNativeBridgeFactory.getBridge();
			await bridge.setSettings({ settings: settingsToRestore });

			const successMessage = `Successfully restored ${category} settings to defaults`;
			SettingsLogger.getInstance().write(
				logConstants.INFO,
				successMessage,
				AotCategory.Info,
				successMessage
			);
			// Invalidate cache so re-visiting this category fetches fresh data from native
			settingsCache.invalidate(category);
		} catch (error: unknown) {
			const errorMessage = `Error restoring ${category} settings: ${error}`;
			SettingsLogger.getInstance().write(
				logConstants.ERROR,
				errorMessage,
				AotCategory.Error,
				errorMessage
			);
			setError(`Failed to restore ${category} settings to defaults`);
		}
	};

	return {
		settingsState,
		availableSettingIds,
		error,
		handleChangeBySettingId,
		isLoading,
		restoreDefaults,
	};
};
